import { useEffect, useMemo } from 'react';
import { Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogContentText, FormControl, FormControlLabel, FormHelperText, Grid, InputLabel, MenuItem, Select, TextField, Typography } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import { RootState, useAppDispatch, useAppSelector } from '../../store/store';
import { AddNewUserBody, addNewUser, handleToggleAddNewUserDialog } from '../../store/usermanagement-slice/usermanagement-slice';
import { enqueueSnackbar } from '../../store/app-slice/app';
import { handleNotificationVariant } from '../notifications/_common';
import { AppRoutes } from '../../axios/endPoints';
import { Header } from '../common/Header';
import { MLRoles } from '../layout/menu-items';
import Transition from '../common/Transition';
import { usePhoneCountries } from '../../utils/usePhoneCountries';
import { PhoneCountryOptionRow } from '../common/PhoneCountryOptionRow';

interface AddNewUserForm {
  firstName: string,
  lastName: string,
  companyName: string,
  jobTitle: string,
  phoneCountryCode: string,
  phoneNationalNumber: string,
  email: string,
  role: string,
  isSubscriptionExempt: boolean
}

const addUserDefaultValues: AddNewUserForm = {
  firstName: '',
  lastName: '',
  companyName: '',
  jobTitle: '',
  phoneCountryCode: 'IT',
  phoneNationalNumber: '',
  email: '',
  role: '',
  isSubscriptionExempt: false
};

const schema = (t : Function) => yup.object().shape({
  firstName: yup.string().required(t('USER_MANAGEMENT_FIRST_NAME_REQUIRED')),
  lastName: yup.string().required(t('USER_MANAGEMENT_LAST_NAME_REQUIRED')),
  companyName: yup.string().required(t('USER_MANAGEMENT_COMPANY_NAME_REQUIRED')),
  jobTitle: yup.string().required(t('USER_MANAGEMENT_JOB_TITLE_REQUIRED')),
  phoneCountryCode: yup.string().required(t('USER_MANAGEMENT_PHONE_PREFIX_REQUIRED')),
  phoneNationalNumber: yup
    .string()
    .required(t('USER_MANAGEMENT_PHONE_NUMBER_REQUIRED'))
    .matches(/^[0-9\s()-]{5,}$/i, t('USER_MANAGEMENT_PHONE_NUMBER_VALIDATION')),
  email: yup.string().email(t('USER_MANAGEMENT_EMAIL_VALIDATION')).required(t('USER_MANAGEMENT_EMAIL_VALIDATION')),
  role: yup.string().required(t('USER_MANAGEMENT_ROLE_VALIDATION')),
  isSubscriptionExempt: yup.boolean().default(false)
});

export const AddNewUserDialog = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { addNewUserDialog } = useAppSelector((state: RootState) => state.usermanagement);
  const { countries } = usePhoneCountries();
  const { control, handleSubmit, formState: { errors }, watch, reset } = useForm<AddNewUserForm>({
    mode: 'onBlur',
    resolver: yupResolver(schema(t)),
    defaultValues: addUserDefaultValues
  });

  const watchPhoneCountryCode = watch('phoneCountryCode');

  const countryOptions = useMemo(() => {
    const items = (countries ?? []).map((c) => {
      const translated = t(c.title);
      const label = translated === c.title ? c.code : translated;
      return {
        ...c,
        label: `${label}`.trim(),
      };
    });
    items.sort((a, b) => a.label.localeCompare(b.label));
    return items;
  }, [countries, t]);

  function handleClose() {
    reset(addUserDefaultValues);
    dispatch(handleToggleAddNewUserDialog(false));
  }

  function handleAddNewUser(data: AddNewUserForm)
  {
    const changePasswordUrl = new URL(
      AppRoutes.RESET_PASSWORD,
      window.location.protocol + '//' + window.location.host
    ).href;

    const selectedCountry = countryOptions.find((c) => c.code === watchPhoneCountryCode);
    const dialCode = selectedCountry?.dialCode ?? '';
    const phoneNumber = [dialCode, data.phoneNationalNumber.trim()].filter(Boolean).join(' ').trim();

    const payload: AddNewUserBody = {
      firstName: data.firstName,
      lastName: data.lastName,
      companyName: data.companyName,
      jobTitle: data.jobTitle,
      phoneNumber,
      email: data.email,
      role: data.role,
      changePasswordUrl: changePasswordUrl,
      selfRegistrated: false,
      isSubscriptionExempt: data.isSubscriptionExempt
    };

    dispatch(addNewUser(payload)).then((res : any) => {
        if(res && res.payload) {
          dispatch(enqueueSnackbar({
              message: t(`${res.payload.message}`),
              options: {
                  variant: handleNotificationVariant(res.payload.success),
                  persist: !res.payload.success
              }
          }))
        }
      }
    );
  }

  useEffect(() => {
    if(!addNewUserDialog) {
      reset(addUserDefaultValues);
    }
  }, [addNewUserDialog, reset]);

  return(
    <Dialog
      fullWidth
      maxWidth="sm"
      TransitionComponent={Transition}
      open={addNewUserDialog}
      scroll="paper"
      PaperProps={{
        sx: {
          display: 'flex',
          flexDirection: 'column',
        }
      }}
    >
      <form onSubmit={handleSubmit(handleAddNewUser)}>
        <Header title={t('USER_MANAGEMENT_ADD_NEW_USER')} />
        <DialogContent sx={{ flex: 1, overflowY: 'auto' }}>
          <DialogContentText sx={{ mb: 1 }}>
            {t('USER_MANAGEMENT_ADD_USER_INFO_TEXT')}
          </DialogContentText>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Controller
            control={control}
            name="role"
            render={({ field, fieldState: { error } }) => (
              <FormControl fullWidth size="small" margin="none" error={error !== undefined}>
                <InputLabel id="role-label">{t('USER_MANAGEMENT_ROLE_LABEL')}</InputLabel>
                <Select
                  labelId="role-label"
                  label={t('USER_MANAGEMENT_ROLE_LABEL')}
                  fullWidth
                  variant="outlined"
                  {...field}
                >
                  {Object.values(MLRoles)?.map((value) => {
                    return (<MenuItem key={value} value={value}>{value}</MenuItem>)
                  })}
                </Select>
                <FormHelperText>{error ? errors.role?.message : ''}</FormHelperText>
              </FormControl>
            )}
          />

          <Controller
            control={control}
            name="isSubscriptionExempt"
            render={({ field }) => (
              <Box
                sx={{
                  p: 1.1,
                  borderRadius: 1.5,
                  border: '1px dashed var(--selected-table-row-color)',
                  backgroundColor: 'rgba(0, 0, 0, 0.02)',
                }}
              >
                <FormControlLabel
                  sx={{ m: 0 }}
                  control={<Checkbox checked={field.value} onChange={(event) => field.onChange(event.target.checked)} />}
                  label={t('USER_MANAGEMENT_SUBSCRIPTION_EXEMPT_LABEL')}
                />
                <Typography variant="caption" sx={{ display: 'block', pl: 4.4, color: 'var(--link-text-color)' }}>
                  {t('USER_MANAGEMENT_SUBSCRIPTION_EXEMPT_HELP')}
                </Typography>
              </Box>
            )}
          />

          <Controller
            control={control}
            name="firstName"
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                fullWidth
                margin="none"
                variant="outlined"
                label={t('USER_MANAGEMENT_FIRST_NAME_LABEL')}
                type="text"
                error={error !== undefined}
                helperText={error ? errors.firstName?.message : ''}
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            )}
          />

          <Controller
            control={control}
            name="lastName"
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                fullWidth
                margin="none"
                variant="outlined"
                label={t('USER_MANAGEMENT_LAST_NAME_LABEL')}
                type="text"
                error={error !== undefined}
                helperText={error ? errors.lastName?.message : ''}
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            )}
          />

          <Controller
            control={control}
            name="companyName"
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                fullWidth
                margin="none"
                variant="outlined"
                label={t('USER_MANAGEMENT_COMPANY_NAME_LABEL')}
                type="text"
                error={error !== undefined}
                helperText={error ? errors.companyName?.message : ''}
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            )}
          />

          <Controller
            control={control}
            name="jobTitle"
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                fullWidth
                margin="none"
                variant="outlined"
                label={t('USER_MANAGEMENT_JOB_TITLE_LABEL')}
                type="text"
                error={error !== undefined}
                helperText={error ? errors.jobTitle?.message : ''}
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            )}
          />

          <Controller
            control={control}
            name="email"
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                fullWidth
                margin="none"
                variant="outlined"
                label={t('USER_MANAGEMENT_EMAIL_LABEL')}
                type="text"
                error={error !== undefined}
                helperText={error ? errors.email?.message : ''}
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            )}
          />

          <Grid container spacing={2} alignItems="flex-start">
            <Grid item xs={12} sm={6}>
              <Controller
                control={control}
                name="phoneCountryCode"
                render={({ field, fieldState: { error } }) => (
                  <FormControl fullWidth size="small" margin="none" error={error !== undefined}>
                    <InputLabel id="phone-prefix-label">{t('USER_MANAGEMENT_PHONE_PREFIX_LABEL')}</InputLabel>
                    <Select
                      {...field}
                      labelId="phone-prefix-label"
                      label={t('USER_MANAGEMENT_PHONE_PREFIX_LABEL')}
                      fullWidth
                      variant="outlined"
                      sx={{
                        '& .MuiSelect-select': {
                          display: 'flex',
                          alignItems: 'center',
                          width: '100%',
                          height: '100%',
                        },
                      }}
                      renderValue={(selected) => {
                        const selectedCode = String(selected ?? '');
                        const c = countryOptions.find((x) => x.code === selectedCode);
                        if (!c) return selectedCode;

                        return <PhoneCountryOptionRow country={c} variant="menu" />;
                      }}
                    >
                      {countryOptions.map((c) => (
                        <MenuItem key={c.code} value={c.code}>
                          <PhoneCountryOptionRow country={c} variant="menu" />
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>{error ? errors.phoneCountryCode?.message : ''}</FormHelperText>
                  </FormControl>
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Controller
                control={control}
                name="phoneNationalNumber"
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    fullWidth
                    margin="none"
                    variant="outlined"
                    label={t('USER_MANAGEMENT_PHONE_NUMBER_LABEL')}
                    type="tel"
                    error={error !== undefined}
                    helperText={error ? errors.phoneNationalNumber?.message : ''}
                    size="small"
                    InputLabelProps={{ shrink: true }}
                  />
                )}
              />
            </Grid>
          </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ position: 'sticky', bottom: 0, zIndex: 1, bgcolor: 'background.paper', borderTop: '1px solid', borderColor: 'divider' }}>
          <Button className="button-text-color" onClick={handleClose}>{t('CANCEL_BUTTON')}</Button>
          <Button className="button-text-color" type="submit">{t('SUBMIT_BUTTON')}</Button>
        </DialogActions>
      </form>
    </Dialog>
  )
};
